import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createAPIClient } from '../../api';
import * as types from '../../api/types';
import { PrintLayout, usePrintData } from '../../components/PrintLayout';
import { formatJobDate } from '../../utils/jobPresentation';
import { jobStatusLabel } from '../jobUi';
import { boardSearch, formatArrivalWindow, formatDuration, formatTimeRange, timeOffOn, timeOrdered } from '../dispatchUi';

interface DaySheetData {
  board: types.DispatchBoard;
  company: types.CompanySettings;
}

const cell = 'px-2 py-2 align-top';

/**
 * /print/dispatch?date=&userId= — the technician day sheet (tracker item
 * 40): the day's jobs, time-ordered, with address, arrival window, work and
 * notes. With a userId, that technician only; without, one sheet per
 * technician who has jobs that day (each on its own page). Office roles only,
 * like the board it reads.
 */
export function DispatchPrintPage() {
  const [params] = useSearchParams();
  const date = params.get('date') || undefined;
  const userId = params.get('userId') || undefined;
  const api = useMemo(() => createAPIClient(), []);
  const state = usePrintData<DaySheetData>(`${date}:${userId}`, async () => {
    const [board, company] = await Promise.all([api.getDispatchBoard(date, 'day'), api.getCompanySettings()]);
    return { board, company };
  }, 'Failed to load the day sheet');
  const board = state.data?.board;
  const sheets = useMemo(() => {
    if (!board) return [];
    const people = board.technicians.filter((t) => (userId ? t.id === userId : board.jobs.some((j) => j.assignedTo === t.id)));
    return people.map((tech) => ({ tech, jobs: timeOrdered(board.jobs.filter((j) => j.assignedTo === tech.id && j.status !== 'CANCELLED')) }));
  }, [board, userId]);
  const title = board ? `Day sheet - ${formatJobDate(board.date)}` : 'Day sheet';

  return (
    <PrintLayout title={title} backTo={`/dispatch${boardSearch(date ?? null, 'day')}`} backLabel="Back to dispatch" noun="day sheet" state={state} company={state.data?.company ?? null}>
      {board && sheets.length === 0 && <p>No technician has jobs on {formatJobDate(board.date)}.</p>}
      {board && sheets.map(({ tech, jobs }, index) => (
        <section key={tech.id} className={index > 0 ? 'mt-10 break-before-page' : ''} aria-label={`Day sheet for ${tech.email}`}>
          <div className="print-keep mb-4 flex flex-wrap items-baseline justify-between gap-4">
            <h1 className="text-xl font-bold">DAY SHEET</h1>
            <p><span className="font-semibold">{tech.email}</span> · {formatJobDate(board.date)} · times in {board.timezone}</p>
          </div>
          {timeOffOn(board.timeOff, tech.id, board.date).map((t) => (
            <p key={t.id} className="mb-2 text-sm">Time off{t.startTime ? ` ${t.startTime}-${t.endTime}` : ' all day'}{t.reason ? `: ${t.reason}` : ''}</p>
          ))}
          <table className="print-table w-full border-collapse text-sm" aria-label={`Jobs for ${tech.email}`}>
            <thead>
              <tr className="border-b-2 border-gray-800">
                {['Time', 'Customer and address', 'Work', 'Notes'].map((h) => <th key={h} className="px-2 py-2 text-left font-semibold">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0
                ? <tr><td colSpan={4} className={`${cell} text-gray-600`}>No jobs.</td></tr>
                : jobs.map((job) => (
                  <tr key={job.id} className="border-b border-gray-200">
                    <td className={`${cell} whitespace-nowrap`}>
                      <span className="font-semibold">{formatTimeRange(job.scheduledStartTime, job.durationMinutes)}</span>
                      <span className="block text-xs text-gray-600">{formatDuration(job.durationMinutes)}</span>
                      {job.arrivalWindowMinutes && job.scheduledStartTime && (
                        <span className="block text-xs">Arrive {formatArrivalWindow(job.scheduledStartTime, job.arrivalWindowMinutes)}</span>
                      )}
                    </td>
                    <td className={cell}>
                      <span className="font-semibold">{job.customer.name}</span>
                      <span className="block">{job.property.address}, {job.property.city}, {job.property.state}</span>
                      <span className="block text-xs text-gray-600">{jobStatusLabel(job.status as types.JobStatus)}</span>
                    </td>
                    <td className={cell}>{job.lineSummary || '—'}</td>
                    <td className={`${cell} whitespace-pre-wrap break-words`}>{job.notes?.trim() || ''}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </section>
      ))}
    </PrintLayout>
  );
}
